import { Actions } from '../actions/Actions';
import { isEqual, cloneDeep, transform, isArray, isObject } from 'lodash-es';

// Regex to match ISO 8601 date strings (e.g., "2024-01-15T00:00:00.000Z", "2024-01-15")
// Also matches common date formats like "Mon Jan 15 2024" but NOT arbitrary strings
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z?)?$/;
const DATE_STRING_REGEX = /^[A-Z][a-z]{2}\s[A-Z][a-z]{2}\s\d{1,2}\s\d{4}/;

// Helper function to normalize values for comparison
const normalizeValue = (value: any): any => {
  // Normalize null and empty string as equivalent
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Check if it's a date string or Date object
  if (typeof value === 'string') {
    // Only parse strings that look like actual date formats, not arbitrary strings
    if (ISO_DATE_REGEX.test(value) || DATE_STRING_REGEX.test(value)) {
      const parsedDate = new Date(value);
      if (!isNaN(parsedDate.getTime())) {
        // Return ISO string for consistent comparison
        return parsedDate.toISOString();
      }
    }
  } else if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString();
  }

  return value;
};

// Helper function to normalize values recursively in an object
const normalizeValuesInObject = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return normalizeValue(obj);
  }

  if (obj instanceof Date) {
    return normalizeValue(obj);
  }

  if (isArray(obj)) {
    return obj.map(item => normalizeValuesInObject(item));
  }

  const result: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      result[key] = normalizeValuesInObject(obj[key]);
    }
  }
  return result;
};

// Helper function to remove dynamically added empty fields before comparison
const removeEmptyDynamicFields = (obj: any, reference: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object' || obj instanceof Date) {
    return obj;
  }

  if (isArray(obj)) {
    return obj.map((item, index) => removeEmptyDynamicFields(item, reference?.[index]));
  }

  const result: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // If the key doesn't exist in reference and the value is empty, skip it
      if (reference && !reference.hasOwnProperty(key)) {
        const value = obj[key];
        const isEmpty = value === '' || value === null || value === undefined ||
          (isArray(value) && value.length === 0) ||
          (isObject(value) && !isArray(value) && Object.keys(value).length === 0);
        if (isEmpty) {
          continue; // Skip this dynamically added empty field
        }
      }
      result[key] = removeEmptyDynamicFields(obj[key], reference?.[key]);
    }
  }
  return result;
};

// Fields that are computed from MSX API responses during async loading.
// These fields may differ between InitActionData and UpdatedActionData due to
// async timing (not user changes), so they should be equalized before comparison.
const equalizeApiComputedFields = (initObj: any, updatedObj: any): void => {
  if (initObj?.actionDetails?.opportunityDetails && updatedObj?.actionDetails?.opportunityDetails) {
    // productOpportunitySize is calculated from MSX associatedProducts API response.
    // Init may have a stale value (0) while Updated has the freshly computed value.
    initObj.actionDetails.opportunityDetails.productOpportunitySize =
      updatedObj.actionDetails.opportunityDetails.productOpportunitySize;
  }
};

// Custom comparison that ignores dynamically added empty fields and normalizes values
const isEqualIgnoringDynamicFields = (obj1: any, obj2: any): boolean => {
  const cleaned1 = removeEmptyDynamicFields(obj1, obj2);
  const cleaned2 = removeEmptyDynamicFields(obj2, obj1);

  // Normalize dates, null/empty strings in both objects before comparison
  const normalized1 = normalizeValuesInObject(cleaned1);
  const normalized2 = normalizeValuesInObject(cleaned2);

  // Equalize API-computed fields that may differ due to async MSX data loading
  // These are not user changes and should not trigger the dirty flag
  equalizeApiComputedFields(normalized1, normalized2);

  return isEqual(normalized1, normalized2);
};

const actionReducer = (state: any, action: any) => {
  let reduxState: any;
  let isFormDirty = false;
  let isDifferent = false;
  switch (action.type) {
    case Actions.INIT_ACTION_BASIC_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          actionDetails: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          actionDetails: cloneDeep(action.payload),
        },
        isFormDirty: false,
        hasBeenSaved: false,
      };

    case Actions.UPDATE_ACTION_BASIC_DETAILS:
      const initDetails = state.InitActionData?.actionDetails;

      // Check if this is API refresh bringing in database values (not user changes)
      // This happens when InitActionData has stale cached data and API returns current DB values
      // Only applies during initial load, not after a save operation
      const isAPIDataRefresh =
        initDetails &&
        !state.isFormDirty &&
        !state.hasBeenSaved &&
        action.payload.details?.actionId &&
        (
          // Check if area is being cleared (common in API refresh)
          (action.payload.accountInformation &&
            initDetails.accountInformation?.area &&
            !action.payload.accountInformation.area) ||
          // Check if salesPlay is changing (might be DB value correction or stale API data)
          (action.payload.opportunityDetails &&
            action.payload.opportunityDetails.salesPlay !== undefined &&
            initDetails.opportunityDetails?.salesPlay !== undefined &&
            action.payload.opportunityDetails.salesPlay !== initDetails.opportunityDetails.salesPlay) ||
          // Check if solutionArea is changing (API value correction)
          (action.payload.opportunityDetails &&
            action.payload.opportunityDetails.solutionArea !== undefined &&
            initDetails.opportunityDetails?.solutionArea !== undefined &&
            action.payload.opportunityDetails.solutionArea !== initDetails.opportunityDetails.solutionArea) ||
          // Check if opportunityIntent is changing from array to different array (API value mismatch)
          (action.payload.opportunityDetails &&
            action.payload.opportunityDetails.opportunityIntent &&
            initDetails.opportunityDetails?.opportunityIntent &&
            isArray(action.payload.opportunityDetails.opportunityIntent) &&
            isArray(initDetails.opportunityDetails.opportunityIntent) &&
            !isEqual(action.payload.opportunityDetails.opportunityIntent, initDetails.opportunityDetails.opportunityIntent))
        );

      // Check if multiple fields are being populated during initial async load
      // Only applies during initial load, not after a save operation
      const isInitialAsyncLoad =
        initDetails &&
        !state.isFormDirty &&
        !state.hasBeenSaved &&
        (
          // Check opportunityDetails async fields being populated
          (action.payload.opportunityDetails &&
            ((action.payload.opportunityDetails.solutionArea &&
            (!initDetails.opportunityDetails?.solutionArea || initDetails.opportunityDetails.solutionArea === '')) ||
            (action.payload.opportunityDetails.productSuperRevSumDivision !== undefined &&
                initDetails.opportunityDetails?.productSuperRevSumDivision === undefined) ||
              (action.payload.opportunityDetails.opportunityLink &&
                (!initDetails.opportunityDetails?.opportunityLink || initDetails.opportunityDetails.opportunityLink === '')))) ||
          // Check accountInformation async fields being populated
          (action.payload.accountInformation &&
            ((action.payload.accountInformation.account &&
              (!initDetails.accountInformation?.account || initDetails.accountInformation.account === '')) ||
              (action.payload.accountInformation.eou &&
                (!initDetails.accountInformation?.eou || initDetails.accountInformation.eou === '')) ||
              (action.payload.accountInformation.industry &&
                (!initDetails.accountInformation?.industry || initDetails.accountInformation.industry === '')))) ||
          // Check milestoneDetails async fields being populated
          (action.payload.milestoneDetails &&
            action.payload.milestoneDetails.Customer_Commitment &&
            !initDetails.milestoneDetails?.Customer_Commitment)
        );

      const isUserInitiated = action.meta?.isUserInitiated === true;
      const shouldUpdateInitData =
        (isInitialAsyncLoad || isAPIDataRefresh) && !isUserInitiated;

      reduxState = {
        ...state,
        InitActionData: shouldUpdateInitData
          ? {
            ...state.InitActionData,
            actionDetails: cloneDeep(action.payload),
          }
          : state.InitActionData,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          actionDetails: action.payload,
        },
      };
      isFormDirty =
        !isEqualIgnoringDynamicFields(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return reduxState;

    case Actions.INIT_ACTION_FIELDS:
      return {
        ...state,
        InitActionFields: {
          ...state.InitActionFields,
          actionFields: cloneDeep(action.payload),
        },
      };

    case Actions.REQ_USERS_LIST:
      return {
        ...state,
        ReqUsersList: {
          ...state.ReqUsersList,
          usersList: cloneDeep(action.payload),
        },
      };

    case Actions.INIT_ACTION_VSU_QBC_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          vsuQbcData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          vsuQbcData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_VSU_QBC_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          vsuQbcData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...state,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          vsuQbcData: action.payload,
        },
        isFormDirty,
      };
    case Actions.INIT_ACTION_GTT_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          gttData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gttData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_GTT_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gttData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...state,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gttData: action.payload,
        },
        isFormDirty,
      };

    case Actions.INIT_ACTION_CCE_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          cceData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          cceData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_CCE_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          cceData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          cceData: action.payload,
        },
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          cceData: action.payload,
        },
        isFormDirty,
      };

    case Actions.INIT_ACTION_CSG_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          csgData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          csgData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_CSG_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          csgData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...state,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          csgData: action.payload,
        },
        isFormDirty,
      };

    case Actions.INIT_ACTION_COMPETE_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          competeData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          competeData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_COMPETE_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          competeData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...state,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          competeData: action.payload,
        },
        isFormDirty,
      };

    case Actions.UPDATE_ACTION_GLOBAL_TEAM_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          globalTeamData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.INIT_ACTION_APEX_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          apexData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          apexData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_AOAI_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          aoaiCapacityData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_ECIF_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ecifData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ecifData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_MAAS_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          maaSData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          maaSData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_AHPM_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ahpmData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ahpmData: cloneDeep(action.payload),
        },
      };

    case Actions.INIT_ACTION_GPSINVESTMENTS_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          gpsData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gpsData: cloneDeep(action.payload),
        },
      };
    case Actions.UPDATE_ACTION_APEX_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          apexData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.INIT_ACTION_CET_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          cetData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          cetData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_AOAI_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_ECIF_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ecifData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_MAAS_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          maaSData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.UPDATE_ACTION_GPSINVESTMENTS_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          gpsData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_AHPM_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ahpmData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.ACTION_SAVED:
      return {
        ...state,
        InitActionData: cloneDeep(state.UpdatedActionData),
        isFormDirty: action.payload,
        hasBeenSaved: true,
      };
    case Actions.UPDATE_TAGS:
      return {
        ...state,
        tags: action.payload,
      };
    case Actions.INIT_ACTION_LEFTTAB_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          leftTabData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_LEFTTAB_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState = {
        ...state,
        isFormDirty,
      };
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: action.payload,
        },
        isFormDirty,
      };

    case Actions.UPDATE_ACTION_CET_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          cetData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...state,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          cetData: action.payload,
        },
        isFormDirty,
      };

    case Actions.INIT_ACTION_RELATED_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          relatedData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          relatedData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_RELATED_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          relatedData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return reduxState;

    case Actions.INIT_ACTION_COMMENTS_DETAILS:
      reduxState = {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          commentsData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          commentsData: cloneDeep(action.payload),
        },
      };
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          commentsData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          commentsData: cloneDeep(action.payload),
        },
      };
    case Actions.UPDATE_ACTION_COMMENTS_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          commentsData: action.payload,
        },
      };
      isFormDirty = !isEqual(
        reduxState.InitActionData /*.commentsData.createCommentContent*/,
        reduxState.UpdatedActionData /*.commentsData.createCommentContent*/
      );
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_UPDATE_COMMENTS_DETAILS:
      // Use current state as base instead of replacing entire state with payload snapshot.
      // The payload contains a stale copy of the entire actionReducer state from component props,
      // which may have an outdated isFormDirty value. Only extract commentsData from the payload.
      const commentsPayload = action.payload;
      const updatedCommentsState = {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          commentsData: commentsPayload?.InitActionData?.commentsData
            ? cloneDeep(commentsPayload.InitActionData.commentsData)
            : state.InitActionData?.commentsData,
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          commentsData: commentsPayload?.UpdatedActionData?.commentsData
            ? cloneDeep(commentsPayload.UpdatedActionData.commentsData)
            : state.UpdatedActionData?.commentsData,
        },
      };
      isDifferent = !isEqual(
        updatedCommentsState.InitActionData?.commentsData,
        updatedCommentsState.UpdatedActionData?.commentsData
      );
      updatedCommentsState.isUnsavedDataPromptActive = isDifferent;
      // Recalculate isFormDirty based on full state comparison
      isFormDirty = !isEqual(updatedCommentsState.InitActionData, updatedCommentsState.UpdatedActionData);
      return {
        ...updatedCommentsState,
        isFormDirty,
      };

    case Actions.INIT_ACTION_TAGS_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          tagsData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          tagsData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_TAGS_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          tagsData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      console.log('store update');
      console.log(reduxState);
      return reduxState;

    case Actions.INIT_ACTION_ATTACHMENT_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          attachments: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          attachments: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_ATTACHMENT_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          attachments: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {
        console.log('difference in attachments');
        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.INIT_ACTION_CAE_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          caeData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          caeData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_CAE_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          caeData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.INIT_ACTION_GLOBAL_TEAM_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          globalTeamData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          globalTeamData: cloneDeep(action.payload),
        },
      };
    case Actions.UPDATE_ACTION_GLOBAL_TEAM_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          globalTeamData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.INIT_ACTION_APEX_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          apexData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          apexData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_AOAI_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          aoaiCapacityData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_ECIF_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ecifData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ecifData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_RAID_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          raidData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          raidData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_AHPM_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ahpmData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ahpmData: cloneDeep(action.payload),
        },
      };
    case Actions.UPDATE_ACTION_APEX_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          apexData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.UPDATE_ACTION_AOAI_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_ECIF_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ecifData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_RAID_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          raidData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_AHPM_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ahpmData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.ACTION_SAVED:
      return {
        ...state,
        InitActionData: cloneDeep(state.UpdatedActionData),
        isFormDirty: action.payload,
        hasBeenSaved: true,
      };
    case Actions.UPDATE_TAGS:
      return {
        ...state,
        tags: action.payload,
      };
    case Actions.INIT_ACTION_LEFTTAB_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          leftTabData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_LEFTTAB_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState = {
        ...state,
        isFormDirty,
      };
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: action.payload,
        },
        isFormDirty,
      };
    case Actions.UPDATE_ACTION_GLOBAL_TEAM_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          globalTeamData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.INIT_ACTION_APEX_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          apexData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          apexData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_AOAI_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          aoaiCapacityData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_ECIF_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ecifData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ecifData: cloneDeep(action.payload),
        },
      };
    case Actions.INIT_ACTION_AHPM_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          ahpmData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ahpmData: cloneDeep(action.payload),
        },
      };
    case Actions.UPDATE_ACTION_APEX_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          apexData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.UPDATE_ACTION_AOAI_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          aoaiCapacityData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_ECIF_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ecifData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.UPDATE_ACTION_AHPM_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          ahpmData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.ACTION_SAVED:
      return {
        ...state,
        InitActionData: cloneDeep(state.UpdatedActionData),
        isFormDirty: action.payload,
        hasBeenSaved: true,
      };
    case Actions.UPDATE_TAGS:
      return {
        ...state,
        tags: action.payload,
      };
    case Actions.INIT_ACTION_LEFTTAB_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          leftTabData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_LEFTTAB_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState = {
        ...state,
        isFormDirty,
      };
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          leftTabData: action.payload,
        },
        isFormDirty,
      };

    //#region Nominations
    case Actions.INIT_ACTION_Nominations_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          mwHubData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          mwHubData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_Nominations_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          mwHubData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {
        //console.log('difference in nomination');
        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    //#endregion
    //coSell
    case Actions.INIT_ACTION_COSELL_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          coSellData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          coSellData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_COSELL_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          coSellData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          coSellData: action.payload,
        },
        isFormDirty,
      };
    //#endregion
    case Actions.INIT_ACTION_REDBUTTON_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          redButtonData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          redButtonData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_REDBUTTON_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          redButtonData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          redButtonData: action.payload,
        },
        isFormDirty,
      };
    case Actions.SELECTED_ASSIGNED_TEAM:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          assignedTeamValue: action.payload,
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          assignedTeamValue: action.payload,
        },
      };
    //#region IAT
    case Actions.INIT_ACTION_IAT_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          iatData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          iatData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_IAT_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          iatData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      reduxState = {
        ...state,
        isFormDirty,
      };
      return {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          iatData: action.payload,
        },
        isFormDirty,
      };

    //#region CAPSL
    case Actions.INIT_ACTION_CAPSL_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          capslData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          capslData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_CAPSL_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          capslData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {
        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    case Actions.INIT_ACTION_MACC_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          maccData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          maccData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_MACC_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          maccData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {

        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;
    //#region AMM
    case Actions.INIT_ACTION_AMM_DETAILS:
      return {
        ...state,
        InitActionData: {
          ...state.InitActionData,
          AMMData: cloneDeep(action.payload),
        },
        UpdatedActionData: {
          ...state.UpdatedActionData,
          AMMData: cloneDeep(action.payload),
        },
      };

    case Actions.UPDATE_ACTION_AMM_DETAILS:
      reduxState = {
        ...state,
        UpdatedActionData: {
          ...state.UpdatedActionData,
          AMMData: action.payload,
        },
      };
      isFormDirty = !isEqual(reduxState.InitActionData, reduxState.UpdatedActionData);
      if (isFormDirty) {
        console.log(difference(reduxState.InitActionData, reduxState.UpdatedActionData));
      }
      reduxState = {
        ...reduxState,
        isFormDirty,
      };
      return reduxState;

    case Actions.UPDATE_SELECTED_ACTION_ID:
      return {
        ...state,
        SelectedActionsId: action.payload,
      };
    default:
      return { ...state };
  }
};

function difference(origObj: any, newObj: any) {
  function changes(newObj: any, origObj: any) {
    let arrayIndexCounter = 0;
    return transform(newObj, function (result: any, value: any, key: any) {
      if (!isEqual(value, origObj[key])) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key;
        result[resultKey] =
          isObject(value) && isObject(origObj[key]) ? changes(value, origObj[key]) : value;
      }
    });
  }
  return changes(newObj, origObj);
}

export default actionReducer;
// Generated by GitHub Copilot | Updated on: 2026-08-18 | 5 lines modified | Preserved explicit user edits as dirty
